import { StructuredMetadataTable } from '@backstage/core-components';
import { Card, CardContent, Divider, makeStyles } from '@material-ui/core';

import { FormProvider, UseFormReturn } from 'react-hook-form';
import { AccountDetailsCardProps, DepartmentEntry } from '../types';
import { useAllowedCostCenter } from '../hooks/useAllowedCostCenter';
import { useDataGovernanceTagsEnabled } from '../hooks/useDataGovernanceTags';
import { AlertGroup } from './AlertGroup';
import { AppInventoryId } from './AppInventoryId';
import { CostCenter } from './CostCenter';
import { DataAssetOwner } from './DataAssetOwner';
import { PfgOwner } from './PfgOwner';
import { Department } from './Department';

const useStyles = makeStyles({
  gridItemCard: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% - 10px)', // for pages without content header
    marginBottom: '10px',
  },
  fullHeightCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  gridItemCardContent: {
    flex: 1,
  },
  fullHeightCardContent: {
    flex: 1,
  },
});

interface EditAccountTagsIconDialogProps {
  variant?: AccountDetailsCardProps['variant'];
  formContext: UseFormReturn<any>;
  apiValidationPayload: any;
  isLoading: boolean;
  department_list_filtered: DepartmentEntry[];
  isPfgOrgUnitAvailable: boolean;
}

export function EditAccountTagsIconDialog({
  variant,
  formContext,
  apiValidationPayload,
  isLoading,
  department_list_filtered,
  isPfgOrgUnitAvailable,
}: EditAccountTagsIconDialogProps) {
  const classes = useStyles();
  const { allowedCostCenters } = useAllowedCostCenter();
  const dataGovernanceTagsEnabled = useDataGovernanceTagsEnabled();

  let cardClass = '';
  let cardContentClass = '';
  if (variant === 'gridItem') {
    cardClass = classes.gridItemCard;
    cardContentClass = classes.gridItemCardContent;
  } else if (variant === 'fullHeight') {
    cardClass = classes.fullHeightCard;
    cardContentClass = classes.fullHeightCardContent;
  }
  const label = isPfgOrgUnitAvailable ? 'PFG-Org Unit' : 'Department';

  return (
    <Card className={cardClass}>
      <Divider />
      <CardContent className={cardContentClass}>
        <FormProvider {...formContext}>
          <StructuredMetadataTable
            metadata={{
              'PFG-Cost Center': (
                <>
                  <CostCenter
                    apiValidation={{
                      isReadyForServerSideValidation: false,
                      ...apiValidationPayload,
                    }}
                    disabled={isLoading}
                    costCenters={allowedCostCenters}
                  />
                </>
              ),
              'PFG-App Inventory Id': (
                <>
                  <AppInventoryId
                    apiValidation={{
                      isReadyForServerSideValidation: false,
                      ...apiValidationPayload,
                    }}
                    disabled={isLoading}
                  />
                </>
              ),
              'PFG-Alert Group': (
                <>
                  <AlertGroup
                    apiValidation={{
                      isReadyForServerSideValidation: false,
                      ...apiValidationPayload,
                    }}
                    disabled={isLoading}
                  />
                </>
              ),
              ...(dataGovernanceTagsEnabled && {
                'PFG-Data Asset Owner': (
                  <>
                    <DataAssetOwner
                      apiValidation={{
                        isReadyForServerSideValidation: false,
                        ...apiValidationPayload,
                      }}
                      disabled={isLoading}
                    />
                  </>
                ),
              }),
              'PFG-Owner': (
                <>
                  <PfgOwner
                    apiValidation={{
                      isReadyForServerSideValidation: false,
                      ...apiValidationPayload,
                    }}
                    disabled={isLoading}
                  />
                </>
              ),
              [label]: (
                <>
                  <Department
                    apiValidation={{
                      isReadyForServerSideValidation: false,
                      ...apiValidationPayload,
                    }}
                    disabled={isLoading}
                    departments={department_list_filtered}
                    isPfgOrgUnitAvailable={isPfgOrgUnitAvailable}
                  />
                </>
              ),
            }}
          />
        </FormProvider>
      </CardContent>
    </Card>
  );
}
